import { CircularProgress } from "@mui/material";
import { RouteComponentProps } from "@gatsbyjs/reach-router";
import { APAsyncButton, APCard, APCenter, APColumn, APImage, APRow, APSizedBox, APText, getIt, handleErrorMessage, showConfirmationDialog, showErrorDialog, showSnackbar, useFilePicker } from "ap-components";
import React, { useEffect, useState } from "react";
import { Heading, Icons } from ".";
import { convertBase64, } from "../../Common/Utils";
import SharedPrefs from "../../Services/SharedPrefs";
import MandateModel from "../../Models/Mandate";
import LoadingState from "./LoadingState";
import MandateScrollContainer from "./MandateScrollContainer";
import MutualFundApiServices from "../../Services/MutualFundServices";
import PartnerApiServices from "../../Services/PartnerApiServices";

export default function OfflineInstructions(props: RouteComponentProps<{ mandateId: string }>) {
    var { files, onClick, HiddenFileInput, reset } = useFilePicker();
    const [file, setFile] = useState<File | null>(null);
    const [mandate, setMandate] = useState<MandateModel>();

    useEffect(() => {
        if (files && files[0]) {
            setFile(files[0]);
        }
    }, [files])

    useEffect(() => {
        getMandate()
    }, [])

    if (!props.mandateId) {
        window.history.back();
        showErrorDialog("Please select correct mandate");
        return <></>
    }

    async function getMandate() {
        if (!mandateToken || !props.mandateId) {
            return
        }

        try {
            const res = await getIt(MutualFundApiServices).getMandate(props.mandateId, mandateToken);
            setMandate(res);
        } catch (error) {
            handleErrorMessage(error);
        }
    }

    const mandateToken = getIt(SharedPrefs).getItem('mandateTkn');

    if (!mandateToken) {
        window.location.href = '/';
        showErrorDialog("Unauthorized Access");
        return <></>
    }

    if (!props.mandateId) {
        window.history.back();
        showErrorDialog("MandateId not provided");
        return <></>
    }

    function bytesForHuman(bytes: number) {
        let units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']
        let i = 0

        for (i; bytes > 1024; i++) {
            bytes /= 1024;
        }

        return Math.ceil(bytes).toFixed(1) + ' ' + units[i]
    }

    if (!mandate) {
        return <LoadingState />
    }

    if (file) {
        return (
            <MandateScrollContainer
                scrollContent={
                    <APColumn gap="8px" crossAxisAlignment="stretch">
                        <UploadForm />
                        <APCard style={{ borderRadius: "8px", padding: "8px" }}>
                            <APRow gap="8px" crossAxisAlignment="start">
                                <APSizedBox height="100px" width="100px" style={{ background: "#eee" }}>
                                    <ImageFromFile file={file} />
                                </APSizedBox>
                                <APColumn crossAxisAlignment="start" mainAxisAlignment="start" gap="4px">
                                    <APText style={{ fontSize: "16px", lineHeight: 1.5, color: "#232730" }} >{file.name}</APText>
                                    <APText style={{ fontSize: "14px", lineHeight: 1.43, color: "#606c84" }} >{bytesForHuman(file.size)}</APText>
                                </APColumn>
                                <APSizedBox width="126px" />
                                <img onClick={async () => {
                                    try {
                                        var res = await showConfirmationDialog("Confirmation", "Are you sure you want to remove the copy?")
                                        if (res) {
                                            setFile(null)
                                        }
                                    } catch (error) {
                                    }
                                }} src={"/img/ic_cancel.svg"} />
                            </APRow>
                        </APCard>
                    </APColumn>
                }

                actions={
                    <APAsyncButton
                        onClick={async () => {
                            try {
                                if (!file) {
                                    throw new Error("Please upload mandate image")
                                }
                                var form = new FormData()
                                form.append("bseMandateId", mandate.bseMandateId)
                                form.append("file", file!)

                                await getIt(PartnerApiServices).uploadMandate(form, mandateToken)
                                showSnackbar('Mandate file has been uploaded')
                                reset();
                                window.location.href = `/mandate/${props.mandateId}/complete/offline`
                            } catch (error) {
                                handleErrorMessage(error)
                            }
                        }}
                    >Submit</APAsyncButton>
                }
            />
        )
    }

    return (
        <MandateScrollContainer
            scrollContent={
                <APColumn gap="8px" crossAxisAlignment="stretch">
                    {/* Email will always come in case of mandate */}
                    <OfflineWhatsNext email={mandate.email ?? ""} />
                    <UploadForm />
                </APColumn>
            }

            actions={
                <APAsyncButton
                    onClick={async () => onClick()}
                >
                    <>
                        Upload
                        <HiddenFileInput accept={"image/png, image/jpeg"} />
                    </>
                </APAsyncButton>
            }
        />
    )
}

function ImageFromFile({ file }: { file: File }) {
    const [image, setImage] = useState<string>();
    useEffect(() => {
        getImage();
    }, [])

    async function getImage() {
        const base64Image = await convertBase64(file) as string;
        setImage(base64Image);
    }

    return (
        <APSizedBox height="100px" width="100px" style={{ background: "#eee" }}>
            <APColumn mainAxisAlignment="center">
                {
                    image ?
                        <APImage
                            style={{ borderRadius: "4px", maxWidth: "100px", maxHeight: "100px" }}
                            src={image}
                        />
                        :
                        <APCenter>
                            <CircularProgress />
                        </APCenter>
                }
            </APColumn>
        </APSizedBox>
    )
}

export function OfflineWhatsNext({ email }: { email: string }) {
    return (
        <APCard style={{ borderRadius: "8px", padding: "16px" }}>
            <Heading
                text={"What’s next?"}
                desc={
                    <APText style={{ color: "#41495b", lineHeight: 1.43, fontSize: "14px" }}>
                        AutoPay form will be emailed to: <span style={{ fontWeight: 500, color: "#232730" }}>{email}</span>
                    </APText>
                }
            />
            <APSizedBox height="8px" />
            <Icons icon={"ic_download"} text={"Print the form from your email"} />
            <APSizedBox height="16px" />
            <Icons icon={"ic_signature"} text={"Sign above the highlighted line"} />
            <APSizedBox height="16px" />
            <Icons icon={"ic_upload"} text={"Scan/click a picture and upload the form"} />
        </APCard>
    )
}

export function UploadForm() {
    return (
        <APCard style={{ padding: "16px", borderRadius: "8px" }}>
            <Heading text={"Upload the form"} desc={"Upload your AutoPay form to initiate the process."} />
            <APSizedBox height="18px" />
            <Icons icon={"ic_check"} text={"Check all the details"} />
            <APSizedBox height="12px" />
            <Icons icon={"ic_check"} text={"The signature should match bank records"} />
            <APSizedBox height="12px" />
            <Icons icon={"ic_check"} text={"Upload the complete form"} />
        </APCard>)
}